import { useCallback } from "react";

import { useIntl } from "react-intl";

import { useGuardrailValidation } from "flows/Inventory/shared/hooks/useGuardrailValidation";
import { useGetProductStockQuery } from "flows/Inventory/shared/queries/inventoryEntry/inventoryEntry.generated";
import { useCustomToast } from "shared/hooks/useCustomToast";

import { useGuardrailsFeatureFlag } from "../../flows/StockCorrections/hooks/useGuardrailsFeatureFlag";

export type UseCheckQuantityValidationParams = {
  countedItems: number;
  sku: string | null;
};

export type UseCheckQuantityValidationResult = {
  expectedQuantity: number;
  productPrice: number;
  loading: boolean;
  error: any;
  gap: number;
  quantityExceeded: boolean;
  valueExceeded: boolean;
  shouldShowConfirmation: () => boolean;
  handleQuantityLimitExceeded: () => boolean;
};

export function useCheckQuantityValidation({
  countedItems,
  sku,
}: UseCheckQuantityValidationParams): UseCheckQuantityValidationResult {
  const intl = useIntl();
  const { showToastUI } = useCustomToast();
  const guardrails = useGuardrailsFeatureFlag();

  const { data, loading, error } = useGetProductStockQuery({
    fetchPolicy: "network-only",
    variables: { sku: sku!, isAlwaysInbound: true },
    skip: !sku,
  });

  const expectedQuantity = data?.getProduct?.inventoryEntry?.stock?.shelf ?? 0;
  const productPrice = data?.getProduct?.price ?? 0;

  const { gap, quantityExceeded, valueExceeded, isOverLimit } = useGuardrailValidation({
    expectedValue: expectedQuantity,
    countedValue: countedItems,
    productPrice,
    guardrails: guardrails ?? undefined,
  });

  const handleQuantityLimitExceeded = useCallback((): boolean => {
    if (!guardrails?.quantityChangeLimit) return false;

    if (isOverLimit) {
      showToastUI?.({
        id: "quantity-Limit-eceeded",
        status: "info",
        title: "quantity-Limit-eceeded-title",
        description: intl.formatMessage(
          { id: "quantity-Limit-eceeded-description" },
          { quantityChangeLimit: guardrails.quantityChangeLimit },
        ),
      });
      return true;
    }

    return false;
  }, [guardrails?.quantityChangeLimit, isOverLimit, showToastUI, intl]);

  const shouldShowConfirmation = useCallback((): boolean => {
    if (loading || error) return false;
    return quantityExceeded || valueExceeded;
  }, [loading, error, quantityExceeded, valueExceeded]);

  return {
    expectedQuantity,
    productPrice,
    loading,
    error,
    gap,
    quantityExceeded,
    valueExceeded,
    shouldShowConfirmation,
    handleQuantityLimitExceeded,
  };
}
